import React, { useState } from 'react';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EstimateCalculator from '../components/EstimateCalculator';
import { motion, AnimatePresence } from 'framer-motion';

const { 
  FiCheck, 
  FiCalendar, 
  FiEdit3, 
  FiClock, 
  FiVideo, 
  FiStar, 
  FiPlus, 
  FiArrowRight, 
  FiTrendingUp, 
  FiTarget, 
  FiInfo 
} = FiIcons;

export default function SocialMediaManagement() {
  const [isMobileCalcOpen, setIsMobileCalcOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  const features = [
    {
      title: "Content Calendar + Strategy",
      desc: "A simple monthly plan built around the 2–2–1 mix (engagement, education, promotion) so you stay consistent and top of mind locally.",
      icon: FiCalendar
    },
    {
      title: "Copy + Branded Design",
      desc: "Captions written in your voice + branded templates for posts, carousels, and Reels—so your feed looks professional without extra work on your end.",
      icon: FiEdit3
    },
    {
      title: "Scheduling + Publishing",
      desc: "We schedule and post to Instagram, and republish to Facebook. Every feed post is also shared to Stories for extra visibility.",
      icon: FiClock
    },
    {
      title: "Monthly Loom Recap",
      desc: "A short monthly Loom video + performance snapshot so you know what worked, what we’re improving, and what’s next—no meetings required.",
      icon: FiVideo
    }
  ];

  const faqs = [
    {
      q: "Do you require long-term contracts?",
      a: "No. We work on a rolling monthly basis. You stay because it's working, not because you're locked in."
    },
    {
      q: "How much time will this take from me?",
      a: "Once we onboard, about 30-60 minutes a month for our strategy review and content approval. We handle the rest."
    },
    {
      q: "Do you only work with pet businesses?",
      a: "Yes. Our specialty is the pet industry, which means we already know what your clients care about."
    },
    {
      q: "What is the difference between Brand Awareness and Lead Gen Ads?",
      a: "Brand Awareness Ads help more local pet owners see and recognize your business (visibility + trust). Recommended: $5–$20/day. Lead Generation Ads are built to drive messages, form leads, and booking requests (inquiries now). Recommended: $50–$200/day."
    },
    {
      q: "What is a Quarterly Content Capture Day?",
      a: "A photo + video session every 3 months so you have fresh content for posts and ads. We handle planning + coordination, and you get a library of ready-to-use assets. Quoted based on location + scope."
    }
  ];

  return (
    <>
      <SEO 
        title="Done-for-You Social Media Management for Pet Care | Otso Co" 
        description="High-converting social media management for dog walkers, groomers, and pet businesses. Get a free estimate in 60 seconds." 
        path="/services/social-media-management" 
      />

      {/* Hero Section */}
      <section className="bg-brand-primaryDark text-brand-white py-24 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <SafeIcon icon={FiTrendingUp} className="w-full h-full" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              You Focus on Pets,<br /> 
              <span className="text-brand-blush">We'll Focus on the Posts.</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Full-service social media management designed to turn your local pet business into the neighborhood's most trusted name.
            </p>
            <div className="flex flex-wrap gap-4">
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium">
                <SafeIcon icon={FiCheck} className="text-brand-blush" /> No Contracts
              </span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium">
                <SafeIcon icon={FiCheck} className="text-brand-blush" /> Pet Industry Experts
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Two-Column Content */}
      <section className="py-20 px-4 bg-brand-white">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16">
          
          {/* Left Column: Content */}
          <div className="flex-grow lg:max-w-[calc(100%-460px)]">
            <div className="space-y-24">
              
              {/* Benefits Section */}
              <div id="benefits">
                <h2 className="text-3xl font-bold mb-12 text-brand-primaryDark">Stop Wasting Hours on Social Media.</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-brand-blush/20 rounded-xl flex items-center justify-center text-brand-primaryDark">
                      <SafeIcon icon={FiClock} size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-brand-primaryDark">Get 16+ Hours Back</h3>
                    <p className="text-gray-600">Stop stressing about captions and hashtags. Spend that time with your pets or growing your actual operations.</p>
                  </div>
                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-brand-mediumPurple/20 rounded-xl flex items-center justify-center text-brand-mediumPurple">
                      <SafeIcon icon={FiTarget} size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-brand-primaryDark">Hyper-Local Reach</h3>
                    <p className="text-gray-600">We don't just go for "viral." We target pet parents in your specific zip codes who can actually book your services.</p>
                  </div>
                </div>
              </div>

              {/* Features Grid */}
              <div>
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-brand-primaryDark">What's Included in Every Social Plan</h2>
                  <p className="text-gray-500 mt-2 font-medium">Ads management and Quarterly Content Days are optional add-ons.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {features.map((f, i) => (
                    <div key={i} className="p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:shadow-md transition-all">
                      <div className="text-brand-mediumPurple mb-4">
                        <SafeIcon icon={f.icon} size={32} />
                      </div>
                      <h4 className="text-lg font-bold text-brand-primaryDark mb-2">{f.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                    </div>
                  ))}
                </div>
                
                {/* Optional Add-ons Row */}
                <div className="mt-12 pt-8 border-t border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                    <span className="text-sm font-bold text-brand-primaryDark uppercase tracking-wider">Optional Add-Ons</span>
                    <div className="flex flex-wrap gap-2">
                      {["Brand Awareness Ads", "Lead Generation Ads", "Quarterly Content Capture Day"].map((pill) => (
                        <span key={pill} className="bg-brand-mediumPurple/10 text-brand-mediumPurple px-4 py-1.5 rounded-full text-xs font-bold border border-brand-mediumPurple/20">
                          {pill}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-4 flex items-center gap-1">
                    <SafeIcon icon={FiInfo} size={12} /> Available in the estimator.
                  </p>
                </div>
              </div>

              {/* Testimonial Section */}
              <div className="bg-brand-primaryDark rounded-3xl p-10 text-white shadow-2xl">
                <div className="flex items-center gap-2 text-brand-blush mb-6">
                  {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FiStar} />)}
                </div>
                <h2 className="text-2xl font-bold mb-6 leading-tight">
                  "If you want to level up your social media presence talk to Perry"
                </h2>
                <p className="text-lg text-gray-300 italic mb-8 leading-relaxed">
                  "Perry listened to my goals and executed my vision for my brand's Instagram, TikTok and Facebook accounts. Our engagement and visibility have sky-rocketed ever since"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-1 border-t-2 border-brand-blush"></div>
                  <p className="font-bold text-brand-blush tracking-wide uppercase text-sm">Taice M.</p>
                </div>
              </div>

              {/* FAQ Section */}
              <div>
                <h2 className="text-3xl font-bold mb-8 text-brand-primaryDark">Common Questions</h2>
                <div className="space-y-4">
                  {faqs.map((faq, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                      <button 
                        onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                        className="w-full flex justify-between items-center p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-bold text-brand-primaryDark">{faq.q}</span>
                        <SafeIcon 
                          icon={FiPlus} 
                          className={`transition-transform duration-200 ${activeFaq === i ? 'rotate-45' : ''}`} 
                        />
                      </button>
                      <AnimatePresence>
                        {activeFaq === i && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                          >
                            <div className="p-6 pt-0 text-gray-600 border-t border-gray-50">
                              {faq.a}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Right Column: Sticky Calculator (Desktop Only) */}
          <div className="hidden lg:block w-[420px] relative">
            <div className="sticky top-[100px]">
              <EstimateCalculator />
            </div>
          </div>

        </div>
      </section>

      {/* Mobile Sticky Button & Modal */}
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-full px-4">
        <button 
          onClick={() => setIsMobileCalcOpen(true)}
          className="w-full bg-brand-mediumPurple text-white py-4 rounded-xl font-bold shadow-2xl flex items-center justify-center gap-3 animate-bounce"
        >
          Get My Free Estimate <SafeIcon icon={FiArrowRight} />
        </button>
      </div>

      <AnimatePresence>
        {isMobileCalcOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="absolute inset-0 bg-brand-primaryDark/80 backdrop-blur-sm" onClick={() => setIsMobileCalcOpen(false)} />
            <motion.div 
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="absolute bottom-0 left-0 w-full h-[90vh] bg-white rounded-t-[32px] overflow-hidden"
            >
              <EstimateCalculator isModal onClose={() => setIsMobileCalcOpen(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}